import { FC, useState } from "react";
import * as _ from "lodash";

import { MetricEntity } from "@ctra/api";
import { useTranslation, Enterprise as Content } from "@ctra/i18n";
import { SearchOutlined, AntDesignSelect as Select, Analytics } from "@ctra/components";

import styles from "./MetricPicker.module.less";
import { useDataDictionary } from "@base";

const { OptGroup, Option } = Select;
const {
  V3: { CategoryIcon }
} = Analytics;

interface MetricPickerProps {
  onChange: (value: MetricEntity["id"]) => void;
  value?: MetricEntity["id"];
  selection?: Array<MetricEntity["id"]>;
}
/**
 * Metric Picker component
 * @param param0
 * @returns
 */
export const MetricPicker: FC<MetricPickerProps> = ({ value, selection, onChange }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<string>("");

  const {
    metrics,
    groups: { byFamilyCategory: groupedMetrics, families }
  } = useDataDictionary();

  const {
    layouts: { section },
    analytics: {
      config: {
        search: { placeholder },
        metric: { displayName }
      }
    }
  } = Content;

  /**
   * Get the dropdown data
   * @type {{}[]}
   */
  const items = _.map(_.keys(groupedMetrics), (category, idx) => ({
    key: idx,
    label: t<string>(section.title(category), { makeDefaultValue: true }),
    options: _.map(_.keys(_.get(groupedMetrics, [category])), (key) => {
      const lookup = [category, key, 0];

      const familyName = _.get(groupedMetrics, [...lookup, "family"]);
      const name = _.get(groupedMetrics, [...lookup, "name"]);
      const id = _.get(groupedMetrics, [...lookup, "id"]);

      return {
        key: _.defaultTo(familyName, id),
        value: _.defaultTo(familyName, id),
        label: t(displayName(_.defaultTo(familyName, name)), { makeDefaultValue: true })
      };
    })
  }));

  /**
   * Filter the options based on search query
   */
  const filteredOptions = items.reduce((acc: typeof items, data) => {
    const filteredGroupOptions = data.options.filter((option) =>
      option.label.toLowerCase().includes(filter.toLowerCase())
    );

    if (filteredGroupOptions.length > 0) {
      acc.push({
        ...data,
        options: filteredGroupOptions
      });
    }

    return acc;
  }, []);

  return (
    <div className={styles.Space}>
      {value ? (
        <CategoryIcon
          category={_.get(families, [value, 0, "category"], _.get(metrics, [value, "category"]))}
          className={styles.Icon}
        />
      ) : (
        <SearchOutlined className={styles.Icon} />
      )}
      <Select
        size="large"
        style={{ width: "100%" }}
        placeholder={t(placeholder)}
        onSearch={setFilter}
        onChange={onChange}
        value={value}
        filterOption={false}
        showSearch
        showArrow={false}
      >
        {filteredOptions.map(({ label, options }) => {
          const ommitedOptions = _.filter(options, ({ value }) => !_.includes(selection, value));
          return (
            <OptGroup key={label} label={label}>
              {ommitedOptions.map(({ label, value }) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </OptGroup>
          );
        })}
      </Select>
    </div>
  );
};
